import { useEffect, useMemo, useRef, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { Send } from 'lucide-react';
import type { LiveSummary, Platform } from '@multimod/shared';
import { DestinationChip } from './DestinationChip.js';
import { Button } from '@/components/ui/button';
import { createChatFeedStore, type ChatFeedStore } from '@/features/chat-feed/store';
import { useBroadcast } from '@/features/moderation/hooks';
import { usePanelStore } from '@/features/panel/store/panel-store';
import { useBroadcastHotkeys } from '@/features/panel/hooks/useBroadcastHotkeys';

function statusText(destinations: Platform[]): string {
  if (destinations.length === 0) return 'Nenhum destino selecionado';
  if (destinations.length === 2) return 'Broadcast nos dois';
  return destinations[0] === 'twitch' ? 'Só Twitch' : 'Só YouTube';
}

function placeholderText(destinations: Platform[]): string {
  if (destinations.length === 0) return 'Selecione ao menos um destino…';
  if (destinations.length === 2) return 'Mensagem pros dois chats…';
  return destinations[0] === 'twitch' ? 'Mensagem pra Twitch…' : 'Mensagem pro YouTube…';
}

export function BroadcastComposer({
  activeLives = [],
  store,
}: {
  activeLives?: LiveSummary[];
  store?: ChatFeedStore;
}) {
  const [draft, setDraft] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fallbackStore = useMemo(() => createChatFeedStore(), []);
  const broadcast = useBroadcast(store ?? fallbackStore);
  const destinations = usePanelStore((s) => s.broadcastDestinations);
  const setDestinations = usePanelStore((s) => s.setBroadcastDestinations);
  const targets = destinations.flatMap((platform) => {
    const live = activeLives.find((item) => item.platform === platform);
    return live ? [{ platform, liveChatId: live.liveChatId }] : [];
  });
  const limit = targets.some((target) => target.platform === 'youtube') ? 200 : 500;
  const trimmed = draft.trim();
  const isTooLong = draft.length > limit;
  // #116: contador some no repouso (prototype não exibe contador). Aparece só
  // perto do limite (>70%) — que já cobre o caso de estouro (isTooLong ⇒ >70%) —
  // pra dar feedback quando importa, sem ruído.
  const showCounter = draft.length > limit * 0.7;

  const sendEnabled = trimmed.length > 0 && targets.length > 0 && !isTooLong && !broadcast.isPending;

  function send() {
    if (!sendEnabled) return;
    broadcast.mutate({ text: trimmed, targets });
    setDraft('');
  }

  useBroadcastHotkeys({ onSend: send, enabled: sendEnabled });

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const isTyping = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;
      if (e.key === '/' && !isTyping) {
        e.preventDefault();
        textareaRef.current?.focus();
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  function toggle(p: Platform) {
    setDestinations(
      destinations.includes(p) ? destinations.filter((x) => x !== p) : [...destinations, p],
    );
  }

  return (
    <form
      aria-label="Compositor de broadcast"
      className="border-t border-border bg-bg-elevated"
      onSubmit={(e) => {
        e.preventDefault();
        send();
      }}
    >
      {/* #86: conteúdo centrado em max-w-[1080px] pra acompanhar a coluna do
          feed. A barra (bg/border) fica full-width; o conteúdo alinha
          verticalmente com feed e summary em telas largas (≥1440px). */}
      <div className="mx-auto max-w-[1080px] w-full px-4 py-3">
      {/* #110: foot do composer espelha screen-panel-1.html .composer-foot —
          label "Destinos", chips, status e hint "⌘B alterna destinos". */}
      <div className="flex items-center gap-2 mb-2" role="group" aria-labelledby="broadcast-dest-label">
        {/* aria-labelledby casa o nome acessível do grupo com o texto visível
            (evita rótulo duplicado de aria-label + span). */}
        <span
          id="broadcast-dest-label"
          className="text-2xs font-mono uppercase tracking-[0.06em] text-fg-subtle mr-0.5"
        >
          Destinos
        </span>
        <div className="flex items-center gap-2">
          <DestinationChip
            platform="twitch"
            active={destinations.includes('twitch')}
            onToggle={() => toggle('twitch')}
          />
          <DestinationChip
            platform="youtube"
            active={destinations.includes('youtube')}
            onToggle={() => toggle('youtube')}
          />
        </div>
        <span
          id="broadcast-status"
          role="status"
          aria-live="polite"
          className={
            'text-xs ' +
            (destinations.length === 0 ? 'text-warning' : 'text-fg-muted')
          }
        >
          {statusText(destinations)}
        </span>
        <div className="ml-auto flex items-center gap-3">
          {showCounter && (
            <span
              id="broadcast-counter"
              role={isTooLong ? 'alert' : undefined}
              className={
                'text-xs tabular-nums ' + (isTooLong ? 'text-warning' : 'text-fg-muted')
              }
            >
              {draft.length}/{limit}
            </span>
          )}
          {/* ⌘B é real: useBroadcastHotkeys → cycleBroadcastDestinations. */}
          <span className="hidden sm:inline-flex items-center gap-1 text-2xs text-fg-subtle whitespace-nowrap">
            <kbd className="text-2xs px-1 py-0.5 bg-bg-muted rounded">⌘B</kbd>
            alterna destinos
          </span>
        </div>
      </div>

      <div className="flex gap-2 items-end">
        <TextareaAutosize
          ref={textareaRef}
          aria-label="Mensagem do broadcast"
          aria-describedby={showCounter ? 'broadcast-status broadcast-counter' : 'broadcast-status'}
          aria-invalid={isTooLong || undefined}
          minRows={1}
          maxRows={4}
          placeholder={placeholderText(destinations)}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
            if (e.key === 'Escape') {
              e.currentTarget.blur();
            }
          }}
          className={
            'flex-1 bg-bg-input border rounded-md px-3 py-2 text-sm text-fg placeholder:text-fg-subtle resize-none focus:outline-none focus:ring-2 ' +
            (isTooLong
              ? 'border-warning focus:border-warning focus:ring-warning'
              : 'border-border focus:border-border-focus focus:ring-ring')
          }
        />
        <Button
          type="submit"
          variant="primary"
          size="lg"
          disabled={!sendEnabled}
          aria-label="Enviar broadcast"
          title="Envio cross-platform chega junto com as ações de mod"
          className="gap-2 h-11 min-w-[112px]"
        >
          <Send size={14} strokeWidth={1.75} />
          <span className="font-medium">Enviar</span>
          <kbd className="text-2xs opacity-70">⌘⏎</kbd>
        </Button>
      </div>
      </div>
    </form>
  );
}
